<template>
  <div>
    <div class="flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
      <h2>{{ t('additionalStations') }}</h2>
      <CVButton class="w-full md:w-auto" @click.prevent="push(getEmptyRow())">
        {{ t('addStation') }}
      </CVButton>
    </div>

    <div v-if="fields.length" class="mb-4 mt-4 rounded-md bg-white pb-1 pl-4 pr-4 pt-1">
      <div
        v-for="(station, i) in fields"
        :key="i"
        class="relative pb-3 pr-8 pt-3"
        :class="{
          'border-b': i < fields.length - 1,
        }"
      >
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SelectField
            :name="`additionalStations[${i}].id`"
            :label="t('station')"
            :options="stationOptions"
            empty-value-to="null"
          />
          <DatepickerField :name="`additionalStations[${i}].fromDate`" :label="t('fromDate')" />
          <DatepickerField :name="`additionalStations[${i}].untilDate`" :label="t('untilDate')" />
        </div>
        <button
          class="absolute bottom-5 right-0 cursor-pointer p-2 text-lg leading-3 text-bittersweet-600"
          @click.prevent="removeField(i)"
        >
          ×
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import DatepickerField from '@/components/DatepickerField.vue';
import SelectField from '@/components/SelectField.vue';
import type { Station } from '@/entities/station/station.entity';
import { useI18n } from 'vue-i18n';
import { useFieldArray } from 'vee-validate';
import { computed } from 'vue';
import { useLocalizedFields } from '@/hooks/use-localized-fields';

const props = defineProps<{
  stations: Station[];
}>();

interface AdditionalCarStationFormValues {
  id: string | null;
  fromDate: string | null;
  untilDate: string | null;
}

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const { push, replace, fields } =
  useFieldArray<AdditionalCarStationFormValues>('additionalStations');

const removeField = (index: number) => {
  replace(fields.value.filter((_, i) => i !== index).map((field) => field.value));
};

const stationOptions = computed(() => [
  {
    label: t('selectStation'),
    value: null,
  },
  ...props.stations.map((station) => ({
    label: getLocalizedField(station.info),
    value: station.id,
  })),
]);

const getEmptyRow = (): AdditionalCarStationFormValues => ({
  id: null,
  fromDate: null,
  untilDate: null,
});
</script>

<i18n lang="json">
{
  "en": {
    "additionalStations": "Additional stations",
    "selectStation": "- Select station -",
    "addStation": "+ Add station",
    "fromDate": "From date",
    "untilDate": "Until date"
  },
  "de": {
    "additionalStations": "An weiteren Stationen buchbar",
    "selectStation": "- Station auswählen -",
    "addStation": "+ Station hinzufügen",
    "fromDate": "Ab Datum",
    "untilDate": "Bis Datum"
  },
  "es": {
    "additionalStations": "Estaciones adicionales",
    "selectStation": "- Seleccionar estación -",
    "addStation": "+ Añadir estación",
    "fromDate": "Desde la fecha",
    "untilDate": "Hasta la fecha"
  }
}
</i18n>
